var productName = 'calendar';import Base from '../../../Core/Base.js';
import ObjectHelper from '../../../Core/helper/ObjectHelper.js';
/**
 * @module Scheduler/view/mixin/TimelineEventRendering
 */
/**
 * Functions to handle event rendering (EventModel -> dom elements).
 *
 * @mixin
 */
export default Target => class TimelineEventRendering extends (Target || Base) {
    static get $name() {
        return 'TimelineEventRendering';
    }
    //region Default config
    static get defaultConfig() {
        return {
            resourceMargin : null,
            /**
             * True to size events based on the rowHeight and barMargin settings. Set this to false if you want to
             * control height and top properties via CSS instead.
             * @config {Boolean}
             * @default
             * @category Scheduled events
             */
            managedEventSizing : true,
            /**
             * The CSS class added to an event/assignment when it is newly created
             * in the UI and unsynced with the server.
             * @config {String}
             * @default
             * @private
             * @category CSS
             */
            generatedIdCls : 'b-sch-dirty-new',
            /**
             * The CSS class added to an event when it has unsaved modifications
             * @config {String}
             * @default
             * @private
             * @category CSS
             */
            dirtyCls : 'b-sch-dirty',
            /**
             * The CSS class added to an event when it is currently committing changes
             * @config {String}
             * @default
             * @private
             * @category CSS
             */
            committingCls : 'b-sch-committing',
            /**
             * The CSS class added to an event/assignment when it ends outside of the visible time range.
             * @config {String}
             * @default
             * @private
             * @category CSS
             */
            endsOutsideViewCls : 'b-sch-event-endsoutside',
            /**
             * The CSS class added to an event/assignment when it starts outside of the visible time range.
             * @config {String}
             * @default
             * @private
             * @category CSS
             */
            startsOutsideViewCls : 'b-sch-event-startsoutside',
            /**
             * The CSS class added to an event/assignment when it is not draggable.
             * @config {String}
             * @default
             * @private
             * @category CSS
             */
            fixedEventCls : 'b-sch-event-fixed',
            /**
             * Event style used by default. Events and resources can specify their own style, with priority order being:
             * Event -> Resource -> Scheduler default. Determines the appearance of the event by assigning a CSS class
             * to it. Available styles are:
             *
             * * `'plain'` (default) - flat look
             * * `'border'` - has border in darker shade of events color
             * * `'colored'` - has colored text and wide left border in same color
             * * `'hollow'` - only border + text until hovered
             * * `'line'` - as a line with the text below it
             * * `'dashed'` - as a dashed line with the text below it
             * * `'minimal'` - as a thin line with small text above it
             * * `'rounded'` - minimalistic style with rounded corners
             *
             * In addition, there are two styles intended to be used when integrating with Bryntum Calendar. To match
             * the look of Calendar events, you can use:
             *
             * * `'calendar'` - a variation of the "colored" style matching the default style used by Calendar
             * * `'interday'` - a variation of the "plain" style, for interday events
             *
             * Specify `null` to not apply a default style and take control using custom CSS (easily overridden basic
             * styling will be used).
             *
             * @config {'plain'|'border'|'colored'|'hollow'|'line'|'dashed'|'minimal'|'rounded'|'calendar'|'interday'}
             * @default
             * @category Scheduled events
             */
            eventStyle : 'plain',
            /**
             * Event color used by default. Events and resources can specify their own color, with priority order being:
             * Event -> Resource -> Scheduler default. Available colors are:
             * * red
             * * pink
             * * purple
             * * violet
             * * indigo
             * * blue
             * * cyan
             * * teal
             * * green
             * * lime
             * * yellow
             * * orange
             * * deep-orange
             * * gray
             * * gantt-green (Useful when you want to match the color to the default color in Gantt)
             *
             * Specify `null` to not apply a default color and take control using custom CSS (an easily overridden color
             * will be used to make sure events are still visible).
             *
             * @member {'red'|'pink'|'purple'|'violet'|'indigo'|'blue'|'cyan'|'teal'|'green'|'lime'|'yellow'|'orange'|'deep-orange'|'gray'|'gantt-green'} eventColor
             * @category Scheduled events
             */
            /**
             * The event color used by the Scheduler. Events and resources can specify their own color. See
             * {@link #property-eventColor} for more details.
             *
             * @config {'red'|'pink'|'purple'|'violet'|'indigo'|'blue'|'cyan'|'teal'|'green'|'lime'|'yellow'|'orange'|'deep-orange'|'gray'|'gantt-green'} eventColor
             * @default 'green'
             * @category Scheduled events
             */
            eventColor : 'green',
            /**
             * The width/height (depending on vertical / horizontal mode) of all the time columns.
             * @config {Number}
             * @category Scheduled events
             */
            tickSize : null
        };
    }
    static configurable = {
        /**
         * Controls how much space to leave between stacked event bars in px.
         *
         * Value will be constrained by half the row height in horizontal mode.
         *
         * @prp {Number}
         * @default
         * @category Scheduled events
         */
        barMargin : 10,
        /**
         * Specify `true` to force rendered events/tasks to fill entire ticks. This only affects rendering, start
         * and end dates retain their value on the data level.
         *
         * When enabling `fillTicks` you should consider either disabling EventDrag/TaskDrag and EventResize/TaskResize,
         * or enabling {@link Scheduler/view/mixin/TimelineDateMapper#config-snap}. Otherwise their behaviour might not
         * be what a user expects.
         *
         * @prp {Boolean}
         * @default
         * @category Scheduled events
         */
        fillTicks : false
    }
    //endregion
    //region Settings
    updateFillTicks(fillTicks) {
        if (!this.isConfiguring) {
            this.timeAxis.forceFullTicks = fillTicks && this.snap;
            this.refreshWithTransition();
            this.trigger('stateChange');
        }
    }
    changeBarMargin(margin) {
        ObjectHelper.assertNumber(margin, 'barMargin');
        // bar margin should not exceed half of the row height
        if (this.isHorizontal && this.rowHeight) {
            return Math.min(Math.ceil(this.rowHeight / 2), margin);
        }
        return margin;
    }
    updateBarMargin() {
        if (this.rendered) {
            this.currentOrientation.onBeforeRowHeightChange();
            this.refreshWithTransition();
            this.trigger('stateChange');
        }
    }
    // Documented in SchedulerEventRendering to not show up in Gantt docs
    get resourceMargin() {
        return this._resourceMargin == null ? this.barMargin : this._resourceMargin;
    }
    set resourceMargin(margin) {
        const me = this;
        ObjectHelper.assertNumber(margin, 'resourceMargin');
        // bar margin should not exceed half of the row height
        if (me.isHorizontal && me.rowHeight) {
            margin = Math.min(Math.ceil(me.rowHeight / 2), margin);
        }
        if (me._resourceMargin !== margin) {
            me._resourceMargin = margin;
            if (me.rendered) {
                me.currentOrientation.onBeforeRowHeightChange();
                me.refreshWithTransition();
            }
        }
    }
    /**
     * Get/set the width/height (depending on mode) of all the time columns to the supplied value.
     * There is a limit for the tick size value. Its minimal allowed value is calculated so ticks would fit the available space.
     * Only applicable when {@link Scheduler.view.TimelineBase#config-forceFit} is set to `false`.
     * To set `tickSize` freely skipping that limitation please set {@link Scheduler.view.TimelineBase#config-suppressFit} to `true`.
     * @property {Number}
     * @category Scheduled events
     */
    set tickSize(width) {
        ObjectHelper.assertNumber(width, 'tickSize');
        this.timeAxisViewModel.tickSize = width;
    }
    get tickSize() {
        return this.timeAxisViewModel.tickSize;
    }
    /**
     * Predefined event colors, useful in combos etc.
     * @type {String[]}
     * @category Scheduled events
     */
    static get eventColors() {
        return ['red', 'pink', 'purple', 'violet', 'indigo', 'blue', 'cyan', 'teal', 'green', 'lime', 'yellow', 'orange', 'deep-orange', 'gray'];
    }
    /**
     * Predefined event styles , useful in combos etc.
     * @type {String[]}
     * @category Scheduled events
     */
    static get eventStyles() {
        return ['plain', 'border', 'hollow', 'colored', 'line', 'dashed', 'minimal', 'rounded'];
    }
    //endregion
    // This does not need a className on Widgets.
    // Each *Class* which doesn't need 'b-' + constructor.name.toLowerCase() automatically adding
    // to the Widget it's mixed in to should implement thus.
    get widgetClass() {}
};
